import { ReactNode, useEffect, useState } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBUserCollage, MBUserImage } from '@shared/components/image';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { getController } from '@shared/util/timeout';
import styles from './MentionsMenu.module.css';

export type MentionGroup = { kind: 'group'; data: API.ChannelGroupFragment };
export type MentionUser = { kind: 'user'; data: API.ShortUserFragment };
export type MentionOption = MentionGroup | MentionUser;

export interface MentionsMenuProps {
  anchorName: string;
  options: MentionOption[];
  hasMore: boolean;
  lightDismiss?: boolean;
  onClickUser(user: API.ShortUserFragment): void;
  onClickGroup(group: API.ChannelGroupFragment): void;
}

export function MentionsMenu({
  anchorName,
  options,
  hasMore,
  lightDismiss = false,
  onClickUser,
  onClickGroup,
}: MentionsMenuProps) {
  const [isClosed, setIsClosed] = useState(false);

  // Reopen when user resumes typing triggering an options change
  useEffect(() => {
    setIsClosed(false);
  }, [options]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hovering, setHovering] = useState(false);

  // Reset selection when the list changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [options.length]);

  useEffect(() => {
    const hasOptions = options.length > 0;
    const hasMultipleOptions = options.length > 1;

    // Don't listen for keyboard events if there are no options
    if (!hasOptions || isClosed) {
      return;
    }

    const { signal, abort } = getController();

    const handleUpDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(i => (i + options.length - 1) % options.length);
          break;
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(i => (i + 1) % options.length);
          break;
        default:
          break;
      }
    };

    const handleEnterEscape = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Tab':
        case 'Escape':
          event.preventDefault();
          setIsClosed(true);
          break;
        case 'Enter':
        case 'NumpadEnter': {
          event.preventDefault();
          const option = options[selectedIndex];
          if (!option) {
            break;
          }
          setIsClosed(true);
          if (option.kind === 'group') {
            onClickGroup(option.data);
          } else {
            onClickUser(option.data);
          }
          break;
        }
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleEnterEscape, { signal });
    if (hasMultipleOptions) {
      document.addEventListener('keydown', handleUpDown, { signal });
    }

    return abort;
  }, [isClosed, options, selectedIndex, onClickGroup, onClickUser]);

  useEffect(() => {
    const node = document.querySelector('[data-selected]');
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selectedIndex]);

  if (options.length === 0 || isClosed) {
    return null;
  }

  return (
    <AnchoredSheet
      anchorName={anchorName}
      onClose={() => setIsClosed(true)}
      className={styles.mentionsMenu}
      lightDismiss={lightDismiss}
      trapFocus={false}
      focusFirstInput={false}
    >
      {hasMore && <Menu.Note>{i18n('chat.mentionsMenu.largeWorkspaceNote')}</Menu.Note>}
      {options.map((option, index) => (
        <MentionsMenuItem
          key={getKey(option)}
          option={option}
          selected={!hovering && index === selectedIndex}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          onClick={() => {
            setIsClosed(true);
            if (option.kind === 'group') {
              onClickGroup(option.data);
            } else {
              onClickUser(option.data);
            }
          }}
        />
      ))}
    </AnchoredSheet>
  );
}

interface MentionsMenuItemProps {
  option: MentionOption;
  selected: boolean;
  onMouseEnter(): void;
  onMouseLeave(): void;
  onClick(): void;
}

function MentionsMenuItem({
  option,
  selected,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: MentionsMenuItemProps) {
  const isGroup = option.kind === 'group';
  if (isGroup) {
    return (
      <GroupItem
        group={option}
        selected={selected}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      />
    );
  } else {
    return (
      <UserItem
        user={option}
        selected={selected}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      />
    );
  }
}

interface GroupItemProps extends Omit<MentionsMenuItemProps, 'option'> {
  group: MentionGroup;
}

function GroupItem({ group, selected, onMouseEnter, onMouseLeave, onClick }: GroupItemProps) {
  const name = group.data.name;
  const description = group.data.description ? ` (${group.data.description})` : '';
  const label = `${name}${description}`;
  return (
    <MentionItem
      avatar={<MBUserCollage size="normal" users={(group.data.users ?? []).filter(notNil)} />}
      label={label}
      selected={selected}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    />
  );
}

interface UserItemProps extends Omit<MentionsMenuItemProps, 'option'> {
  user: MentionUser;
}

function UserItem({ user, selected, onMouseEnter, onMouseLeave, onClick }: UserItemProps) {
  const label = `@${user.data.username}`;
  return (
    <MentionItem
      avatar={<MBUserImage size="normal" user={user.data} />}
      label={label}
      selected={selected}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    />
  );
}

interface MentionItemProps {
  avatar: ReactNode;
  label: string;
  selected: boolean;
  onMouseEnter(): void;
  onMouseLeave(): void;
  onClick(): void;
}

function MentionItem({
  avatar,
  label,
  selected,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: MentionItemProps) {
  return (
    <View
      row
      centerVertically
      className={[styles.item, selected && styles.selected]}
      data-selected={selected || undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <View className={styles.avatar}>{avatar}</View>
      <Text size="body1" numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

function getKey(option: MentionOption): string {
  return option.kind === 'group' ? `group-${option.data.id}` : `user-${option.data.id}`;
}
